import {mergeWith} from 'lodash';
import {Store} from 'redux';

import {globalWindow} from '../../libs/shared/helpers/src/browser/browserHelpers';
import {Dictionary} from '../../libs/shared/helpers/src/typescript/typeHelpers';
import {AppState} from '../store/appState';
import {postAsync} from './http/http';
import {TraceEvent} from './trace';

declare global {
  interface Window {
    pollHeartbeatAsync: () => void;
  }
}

/*
 * Setup.
 */

// Send heartbeat 30s after app starts (avoid slowing down init + capture short sessions).
const initialHeartbeatDelay = 30 * 1000;
// Then, ping every 5 mins.
const heartbeatPeriod = 5 * 60 * 1000;
let heartbeatEndpoint: string | undefined;
let heartbeatTimeout: number | undefined;

export function setupHeartbeat(store: Store<AppState>) {
  const {company} = store.getState().data;
  heartbeatEndpoint = `${company.url}/heartbeat2`;

  store.subscribe(() => {
    // Always enabled because the "sendUsageData" preference is honored on the server.
    // This is because teammate activity reports require the usage.
    if (!heartbeatTimeout) {
      heartbeatTimeout = globalWindow.setInstrumentedTimeout(pollHeartbeatAsync, initialHeartbeatDelay);
    }
  });
}

// Send rolling event buffer periodically.

type HeartbeatEvent = TraceEvent;
interface PushEvent {
  handledCount: number;
  unhandledCount: number;
}

const maxPushDeliveryEvents = 50;

let ablyRelativeDeliveryDelayBuffer: Array<number> = [];
let heartbeatBuffer: Array<HeartbeatEvent> = [];
let pushEventBuffer: Dictionary<PushEvent> = {};
let pushDisconnects = 0;
let pushReconnectDuration = 0;

async function pollHeartbeatAsync() {
  if (!heartbeatEndpoint) {
    return;
  }

  globalWindow.clearTimeout(heartbeatTimeout);

  const lastHeartbeatBuffer = heartbeatBuffer;
  const lastPushEventBuffer = pushEventBuffer;

  const lastPushDisconnects = pushDisconnects;
  const lastPushReconnectDuration = pushReconnectDuration;

  const lastAblyRelativeDeliveryDelayBuffer = ablyRelativeDeliveryDelayBuffer;

  heartbeatBuffer = [];
  pushEventBuffer = {};

  pushDisconnects = 0;
  pushReconnectDuration = 0;

  ablyRelativeDeliveryDelayBuffer = [];

  try {
    const body = {
      ts: Date.now(),
      events: lastHeartbeatBuffer,
      push_events: lastPushEventBuffer,
      push_disconnects: lastPushDisconnects,
      push_reconnect_duration: lastPushReconnectDuration,
      ably_relative_delivery_delay: lastAblyRelativeDeliveryDelayBuffer,
    };

    await postAsync(heartbeatEndpoint, body);
  } catch (error) {
    // In case of failure, merge the previous buffer with the new one.
    heartbeatBuffer = [...lastHeartbeatBuffer, ...heartbeatBuffer];

    mergeWith(
      pushEventBuffer,
      lastPushEventBuffer,
      (sourceEvent: PushEvent | undefined, lastEvent: PushEvent): PushEvent => {
        if (!sourceEvent) {
          return lastEvent;
        }

        return {
          handledCount: sourceEvent.handledCount + lastEvent.handledCount,
          unhandledCount: sourceEvent.unhandledCount + lastEvent.unhandledCount,
        };
      },
    );

    pushDisconnects += lastPushDisconnects;
    pushReconnectDuration += lastPushReconnectDuration;
  }

  heartbeatTimeout = globalWindow.setInstrumentedTimeout(pollHeartbeatAsync, heartbeatPeriod);
}

export function reportHeartbeatTraceEvent(event: TraceEvent) {
  heartbeatBuffer.push(event);
}

export function reportPushEvent(eventName: string, isUnhandled: boolean) {
  let pushEventItem: PushEvent = pushEventBuffer[eventName] ?? {
    handledCount: 0,
    unhandledCount: 0,
  };

  if (isUnhandled) {
    pushEventItem = {
      handledCount: pushEventItem.handledCount,
      unhandledCount: pushEventItem.unhandledCount + 1,
    };
  } else {
    pushEventItem = {
      handledCount: pushEventItem.handledCount + 1,
      unhandledCount: pushEventItem.unhandledCount,
    };
  }

  pushEventBuffer = {
    ...pushEventBuffer,
    [eventName]: pushEventItem,
  };
}

export function reportPushConnectivity(connected: boolean, reconnectDuration?: number) {
  if (!connected) {
    pushDisconnects++;
  } else if (reconnectDuration) {
    pushReconnectDuration = Math.max(pushReconnectDuration, reconnectDuration);
  }
}

export function reportPushDeliveryLatency({
  ablyReceivedAt,
  pusherReceivedAt,
}: {
  ablyReceivedAt: number;
  pusherReceivedAt: number;
}) {
  if (ablyRelativeDeliveryDelayBuffer.length > maxPushDeliveryEvents) {
    return;
  }

  ablyRelativeDeliveryDelayBuffer.push(ablyReceivedAt - pusherReceivedAt);
}

globalWindow.pollHeartbeatAsync = pollHeartbeatAsync;
